'use client';

import React, { useState, useEffect, useCallback } from 'react';
import { asObject, type FieldValue } from '../formFieldTypes';

/**
 * GpsStampField — Auto-captures GPS location with accuracy indicator
 * 
 * Fires on mount (or on button press) to capture current lat/lng/accuracy.
 * Displays mini info panel with coordinates and accuracy badge.
 * 
 * options.accuracy_threshold — Minimum accuracy in meters (blocks if worse)
 * options.show_map           — Show mini static map (future: Leaflet/MapBox)
 * options.require_accuracy   — Block if accuracy worse than X meters
 */

type GpsStampOptions = {
      accuracy_threshold?: number;
      show_map?: boolean;
      require_accuracy?: number;
      auto_capture?: boolean;
      [key: string]: unknown;
};

interface GpsStampFieldProps {
  field: {
    key: string;
    label: string;
    required?: boolean;
    readonly?: boolean;
    options?: unknown;
    help_text?: string;
    [key: string]: unknown;
  };
  value: FieldValue; // { lat, lng, accuracy, timestamp, address? }
  onChange: (value: FieldValue) => void;
  isDark: boolean;
}

interface GpsData {
  lat: number;
  lng: number;
  accuracy: number;
  timestamp: string;
  address?: string;
  [key: string]: unknown;
}

export const GpsStampField: React.FC<GpsStampFieldProps> = ({ field, value, onChange, isDark }) => {
  const opts = (field.options ?? {}) as GpsStampOptions;
  const [capturing, setCapturing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const gpsData: GpsData | null = asObject(value)?.lat !== undefined ? (value as GpsData) : null;
  const autoCapture = opts?.auto_capture !== false;
  const requiredAccuracy = opts?.require_accuracy || opts?.accuracy_threshold;
  const accuracyMet = !requiredAccuracy || (gpsData && gpsData.accuracy <= requiredAccuracy);

  const captureLocation = useCallback(async () => {
    if (field.readonly || !navigator.geolocation) {
      setError('Geolocation not available');
      return;
    }

    setCapturing(true);
    setError(null);

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const data: GpsData = {
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          accuracy: Math.round(pos.coords.accuracy),
          timestamp: new Date().toISOString(),
        };
        onChange(data);
        setCapturing(false);
      },
      (err) => {
        setError(
          err.code === 1 ? 'Location permission denied'
          : err.code === 2 ? 'Location unavailable'
          : 'Location timeout — try again'
        );
        setCapturing(false);
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
    );
  }, [field.readonly, onChange]);

  // Auto-capture on mount — deferred to a task so the geolocation request (and
  // its setState) runs outside the effect body.
  useEffect(() => {
    if (!autoCapture || gpsData || field.readonly) return;
    const timer = setTimeout(captureLocation, 0);
    return () => clearTimeout(timer);
  }, [autoCapture, captureLocation, field.readonly, gpsData]);

  const accuracyColor = gpsData
    ? gpsData.accuracy <= 10 ? 'emerald' : gpsData.accuracy <= 30 ? 'amber' : 'red'
    : 'gray';

  return (
    <div className="space-y-2">
      {gpsData ? (
        <div className={`rounded-lg border p-3 ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-2">
              <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                accuracyColor === 'emerald' ? 'bg-emerald-100 dark:bg-emerald-900/50' :
                accuracyColor === 'amber' ? 'bg-amber-100 dark:bg-amber-900/50' :
                'bg-red-100 dark:bg-red-900/50'
              }`}>
                <svg className={`w-4 h-4 ${
                  accuracyColor === 'emerald' ? 'text-emerald-600 dark:text-emerald-400' :
                  accuracyColor === 'amber' ? 'text-amber-600 dark:text-amber-400' :
                  'text-red-600 dark:text-red-400'
                }`} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 0115 0z" />
                </svg>
              </div>
              <div>
                <p className={`text-xs font-mono ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  {gpsData.lat.toFixed(6)}, {gpsData.lng.toFixed(6)}
                </p>
                <p className={`text-[10px] ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                  {new Date(gpsData.timestamp).toLocaleString()}
                </p>
              </div>
            </div>
            <span className={`inline-flex items-center text-[10px] font-medium px-1.5 py-0.5 rounded-full ${
              accuracyColor === 'emerald' ? 'bg-emerald-100 dark:bg-emerald-900 text-emerald-700 dark:text-emerald-300' :
              accuracyColor === 'amber' ? 'bg-amber-100 dark:bg-amber-900 text-amber-700 dark:text-amber-300' :
              'bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-300'
            }`}>
              ±{gpsData.accuracy}m
            </span>
          </div>

          {/* Accuracy warning */}
          {requiredAccuracy && !accuracyMet && (
            <div className="mt-2 flex items-center gap-1.5 text-xs text-red-500 dark:text-red-400">
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126z" />
              </svg>
              Accuracy ({gpsData.accuracy}m) exceeds required {requiredAccuracy}m — re-capture in open area
            </div>
          )}

          {/* Re-capture button */}
          {!field.readonly && (
            <button
              type="button"
              onClick={captureLocation}
              disabled={capturing}
              className={`mt-2 text-xs px-2 py-1 rounded transition-colors ${
                isDark ? 'text-teal-400 hover:bg-teal-950/30' : 'text-teal-600 hover:bg-teal-50'
              }`}
            >
              {capturing ? 'Updating...' : '↻ Re-capture location'}
            </button>
          )}
        </div>
      ) : (
        /* No GPS yet — capture button */
        <button
          type="button"
          onClick={captureLocation}
          disabled={capturing || field.readonly}
          className={`w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 border-dashed text-sm font-medium transition-colors ${
            capturing
              ? isDark ? 'border-teal-600 text-teal-400 bg-teal-950/20' : 'border-teal-400 text-teal-600 bg-teal-50'
              : isDark
                ? 'border-gray-600 text-gray-400 hover:border-teal-500 hover:text-teal-400'
                : 'border-gray-300 text-gray-500 hover:border-teal-500 hover:text-teal-600'
          }`}
        >
          {capturing ? (
            <><svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg> Acquiring GPS...</>
          ) : (
            <><svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 0115 0z" /></svg> Capture GPS Location</>
          )}
        </button>
      )}

      {/* Error */}
      {error && (
        <p className="text-xs text-red-500 dark:text-red-400">{error}</p>
      )}
    </div>
  );
};

export default GpsStampField;
